import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Minus, ShoppingCart, Eye } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import PriceBlock from "@/components/PriceBlock";
import { toast } from "sonner";

const CATS = [
  { key: "all", label: "All" },
  { key: "smartphones", label: "Phones" },
  { key: "tablets", label: "Tablets" },
  { key: "watches", label: "Watches" },
  { key: "accessories", label: "Accessories" },
  { key: "uncategorised", label: "New Arrivals / Unmapped" },
];

// Global cart cache stored in-memory + sessionStorage
const CART_KEY = "bhawar_cart";
export const readCart  = () => { try { return JSON.parse(sessionStorage.getItem(CART_KEY) || "{}"); } catch { return {}; } };
export const writeCart = (c) => sessionStorage.setItem(CART_KEY, JSON.stringify(c));

// Roles allowed to add items to cart / place orders
const CAN_ORDER = ["dealer", "admin"];

import { useQuery } from "@tanstack/react-query";

export default function Catalogue() {
  const [cat, setCat]         = useState("all");
  const [q, setQ]             = useState("");
  const [cart, setCart]       = useState(readCart());
  // Desktop filters
  const [filterSubCat, setFilterSubCat]   = useState("all");
  const [filterColor, setFilterColor]     = useState("all");
  const [filterStorage, setFilterStorage] = useState("all");
  const [inStockOnly, setInStockOnly]     = useState(true);

  const navigate = useNavigate();
  const { user } = useAuth();
  const canOrder = CAN_ORDER.includes(user?.role);

  const { data: products = [], isLoading: loading } = useQuery({
    queryKey: ["products", cat, q],
    queryFn: async () => {
      const { data } = await api.get("/products", { params: { category: cat, q } });
      return data;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes in memory cache
    keepPreviousData: true, // Smooth transitions between filters
  });

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;

  // Unique filter options derived from current product list
  const subCats  = useMemo(() => ["all", ...new Set(products.map(p => p.sub_category).filter(Boolean))], [products]);
  const colors   = useMemo(() => ["all", ...new Set(products.map(p => p.color).filter(Boolean))], [products]);
  const storages = useMemo(() => ["all", ...new Set(products.map(p => p.storage).filter(Boolean))], [products]);

  // Client-side desktop sub-filters
  const filtered = useMemo(() => {
    return products.filter(p => {
      if (filterSubCat  !== "all" && p.sub_category !== filterSubCat) return false;
      if (filterColor   !== "all" && p.color        !== filterColor)  return false;
      if (filterStorage !== "all" && p.storage      !== filterStorage) return false;
      if (inStockOnly) {
        const avail = p.available_stock !== undefined ? p.available_stock : p.stock;
        if (avail <= 0) return false;
      }
      return true;
    });
  }, [products, filterSubCat, filterColor, filterStorage, inStockOnly]);

  // Pagination calculation
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  // Reset to page 1 on filter changes
  useEffect(() => {
    setPage(1);
  }, [cat, q, filterSubCat, filterColor, filterStorage, inStockOnly]);

  const setQty = (product_id, qty) => {
    const next = { ...cart };
    if (qty <= 0) delete next[product_id];
    else next[product_id] = qty;
    setCart(next); writeCart(next);
  };

  const cartCount = Object.values(cart || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const cartTotal = useMemo(() => {
    if (!Array.isArray(products) || !cart) return 0;
    return products.reduce((sum, p) => {
      const q = cart[p.product_id] || 0;
      const price = parseFloat(p.dealer_price || p.mrp || 0);
      return sum + (q * price);
    }, 0);
  }, [cart, products]);

  const goToBuilder = () => {
    if (cartCount === 0) { toast.error("Add products first"); return; }
    navigate("/po/new");
  };

  // Reset sub-filters when category changes
  const handleCatChange = (v) => {
    setCat(v);
    setFilterSubCat("all");
    setFilterColor("all");
    setFilterStorage("all");
  };

  // ─── Shared card component (used on mobile always, and desktop when no filter panel needed) ───
  const ProductCard = ({ p }) => {
    const qty    = cart[p.product_id] || 0;
    const avail  = p.available_stock !== undefined ? p.available_stock : p.stock;
    const inStock = avail > 0;
    return (
      <Card key={p.product_id} className="p-3 sm:p-4 flex flex-col justify-between" data-testid={`prod-card-${p.sku}`}>
        <div className="flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-mono font-semibold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
              SAP: {p.sap_item_code || p.sku}
            </span>
          </div>
          <div className="mt-1.5 font-medium text-xs sm:text-sm leading-snug line-clamp-2" title={p.name}>
            {p.friendly_name || p.name}
          </div>
          {p.friendly_name && p.friendly_name !== p.name && (
            <div className="text-[10px] text-slate-500 font-mono mt-0.5 line-clamp-1" title={p.name}>
              SAP Desc: {p.name}
            </div>
          )}
          <div className="text-[11px] sm:text-xs text-slate-500 mt-1">
            {p.color ? p.color : ""}{p.storage ? ` · ${p.storage}` : ""}
            {!p.color && !p.storage && <span className="text-amber-600 text-[10px]">Pending Metadata</span>}
          </div>
        </div>
        <div className="mt-2 sm:mt-3 flex flex-col items-start gap-2 w-full">
          <PriceBlock product={p} />
          {user?.role === "dealer" ? (
            <Badge variant="outline" className={`shrink-0 text-[10px] sm:text-xs ${inStock ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-rose-700 border-rose-200 bg-rose-50"}`}>
              {inStock ? "In Stock" : "Out of stock"}
            </Badge>
          ) : (
            <Badge variant="outline" className={`shrink-0 text-[10px] sm:text-xs ${inStock ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-rose-700 border-rose-200 bg-rose-50"}`}>
              {inStock ? `${avail} avail` : "Out of stock"}
            </Badge>
          )}
        </div>
        <div className="mt-3 flex items-center gap-2">
          {canOrder ? (
            qty === 0 ? (
              <Button onClick={() => setQty(p.product_id, 1)} disabled={!inStock} className="w-full h-9 bg-slate-900 text-white hover:bg-slate-800" data-testid={`add-${p.sku}`}>
                <Plus size={14} className="mr-1" /> Add to PO
              </Button>
            ) : (
              <div className="flex items-center justify-between w-full border border-slate-200 rounded-md bg-white">
                <button onClick={() => setQty(p.product_id, qty - 1)} className="p-2 hover:bg-slate-50" data-testid={`dec-${p.sku}`}><Minus size={14} /></button>
                <input
                  type="number"
                  min="0"
                  value={qty}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setQty(p.product_id, isNaN(val) ? 0 : val);
                  }}
                  className="w-14 text-center text-sm font-medium border-0 focus:ring-0 focus:outline-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  data-testid={`qty-input-${p.sku}`}
                />
                <button onClick={() => setQty(p.product_id, qty + 1)} className="p-2 hover:bg-slate-50" data-testid={`inc-${p.sku}`}><Plus size={14} /></button>
              </div>
            )
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Eye size={12} /> View only
            </div>
          )}
        </div>
      </Card>
    );
  };

  // ─── Desktop list row ───────────────────────────────────────────────────────
  const ProductRow = ({ p }) => {
    const qty    = cart[p.product_id] || 0;
    const avail  = p.available_stock !== undefined ? p.available_stock : p.stock;
    const inStock = avail > 0;
    return (
      <tr className="hover:bg-slate-50/70 border-b border-slate-100" data-testid={`prod-row-${p.sku}`}>
        <td className="px-4 py-3">
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-xs font-semibold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded w-fit">
              {p.sap_item_code || p.sku}
            </span>
            <span className="font-medium text-sm text-slate-900 mt-1 leading-snug">
              {p.friendly_name || p.name}
            </span>
            {p.friendly_name && p.friendly_name !== p.name && (
              <span className="text-[11px] text-slate-500 font-mono line-clamp-1" title={p.name}>
                SAP: {p.name}
              </span>
            )}
            {p.sub_category && (
              <span className="text-xs text-slate-400 mt-0.5">{p.sub_category}</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-xs text-slate-600 capitalize whitespace-nowrap">{p.category || "—"}</td>
        <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{p.color || "—"}</td>
        <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{p.storage || "—"}</td>
        <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{p.ram || "—"}</td>
        <td className="px-4 py-3 text-right">
          <PriceBlock product={p} compact />
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-center">
          {user?.role === "dealer" ? (
            <Badge variant="outline" className={`text-[10px] ${inStock ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-rose-700 border-rose-200 bg-rose-50"}`}>
              {inStock ? "In Stock" : "Out of stock"}
            </Badge>
          ) : (
            <Badge variant="outline" className={`text-[10px] ${inStock ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-rose-700 border-rose-200 bg-rose-50"}`}>
              {inStock ? `${avail} avail` : "Out of stock"}
            </Badge>
          )}
        </td>
        <td className="px-4 py-3 text-right whitespace-nowrap">
          {canOrder ? (
            qty === 0 ? (
              <Button size="sm" onClick={() => setQty(p.product_id, 1)} disabled={!inStock} className="bg-slate-900 text-white hover:bg-slate-800 h-8 text-xs" data-testid={`add-${p.sku}`}>
                <Plus size={13} className="mr-1" /> Add
              </Button>
            ) : (
              <div className="inline-flex items-center gap-1 border border-slate-200 rounded-md bg-white">
                <button onClick={() => setQty(p.product_id, qty - 1)} className="px-2 py-1 hover:bg-slate-50 text-xs" data-testid={`dec-${p.sku}`}><Minus size={12} /></button>
                <input
                  type="number"
                  min="0"
                  value={qty}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setQty(p.product_id, isNaN(val) ? 0 : val);
                  }}
                  className="w-10 text-center text-xs font-medium border-0 focus:ring-0 focus:outline-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  data-testid={`qty-input-row-${p.sku}`}
                />
                <button onClick={() => setQty(p.product_id, qty + 1)} className="px-2 py-1 hover:bg-slate-50 text-xs" data-testid={`inc-${p.sku}`}><Plus size={12} /></button>
              </div>
            )
          ) : (
            <span className="text-xs text-slate-400">View only</span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6" data-testid="catalogue-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Samsung Catalogue</div>
          <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
            Browse {canOrder ? "& build a purchase order" : "products"}
          </h1>
          {!canOrder && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 mt-2 inline-block">
              View only — order placement is available to dealers
            </p>
          )}
        </div>
        {canOrder && (
          <Button onClick={goToBuilder} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="catalogue-review-cart-btn">
            <ShoppingCart size={16} className="mr-2" /> Review cart ({cartCount})
          </Button>
        )}
      </div>

      {/* ─── STICKY / FIXATED FILTER BAR (Pinned right under fixed 56px topbar) ──────── */}
      <div className="sticky top-14 z-20 bg-slate-50/95 backdrop-blur py-3 border-b border-slate-200 space-y-3 -mx-4 px-4 sm:-mx-6 sm:px-6 shadow-xs">
        {/* Category tabs + search */}
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={cat} onValueChange={handleCatChange}>
            <TabsList className="bg-slate-200/60">
              {CATS.map(c => <TabsTrigger key={c.key} value={c.key} data-testid={`cat-tab-${c.key}`}>{c.label}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          <div className="relative flex-1 max-w-md ml-auto">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search SKU or product..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" data-testid="catalogue-search" />
          </div>
        </div>

        {/* Filter options bar */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Filter:</span>
          <Select value={filterSubCat} onValueChange={setFilterSubCat}>
            <SelectTrigger className="w-44 h-8 text-xs bg-white" data-testid="filter-subcat">
              <SelectValue placeholder="Sub-category" />
            </SelectTrigger>
            <SelectContent>
              {subCats.map(s => <SelectItem key={s} value={s} className="text-xs">{s === "all" ? "All sub-categories" : s}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={filterColor} onValueChange={setFilterColor}>
            <SelectTrigger className="w-36 h-8 text-xs bg-white" data-testid="filter-color">
              <SelectValue placeholder="Color" />
            </SelectTrigger>
            <SelectContent>
              {colors.map(c => <SelectItem key={c} value={c} className="text-xs">{c === "all" ? "All colors" : c}</SelectItem>)}
            </SelectContent>
          </Select>

          {storages.length > 1 && (
            <Select value={filterStorage} onValueChange={setFilterStorage}>
              <SelectTrigger className="w-36 h-8 text-xs bg-white" data-testid="filter-storage">
                <SelectValue placeholder="Storage" />
              </SelectTrigger>
              <SelectContent>
                {storages.map(s => <SelectItem key={s} value={s} className="text-xs">{s === "all" ? "All storage" : s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setInStockOnly(!inStockOnly)}
            className={`h-8 text-xs ${inStockOnly ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 font-medium" : "bg-white text-slate-500 hover:bg-slate-50"}`}
            data-testid="filter-instock"
          >
            {inStockOnly ? "✓ In Stock Only" : "Show Out of Stock"}
          </Button>

          {(filterSubCat !== "all" || filterColor !== "all" || filterStorage !== "all" || !inStockOnly) && (
            <button
              onClick={() => { setFilterSubCat("all"); setFilterColor("all"); setFilterStorage("all"); setInStockOnly(true); }}
              className="text-xs text-rose-600 hover:text-rose-800 font-medium"
              data-testid="filter-clear"
            >
              Clear filters
            </button>
          )}

          <span className="ml-auto text-xs text-slate-400">{filtered.length} product{filtered.length !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* ─── DESKTOP: list table ─────────────────────────────────── */}
      <div className="hidden lg:block">
        <Card className="overflow-hidden border border-slate-200 shadow-xs">
          <div className="overflow-x-auto max-h-[calc(100vh-250px)] overflow-y-auto">
            <table className="w-full text-sm min-w-[900px] border-collapse">
              <thead className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur text-[10px] uppercase tracking-wider text-slate-600 border-b border-slate-200 shadow-xs">
                <tr>
                  <th className="text-left px-4 py-3 bg-slate-100/90 w-[30%]">Product & SAP Code</th>
                  <th className="text-left px-4 py-3 bg-slate-100/90 w-[10%]">Category</th>
                  <th className="text-left px-4 py-3 bg-slate-100/90 w-[8%]">Color</th>
                  <th className="text-left px-4 py-3 bg-slate-100/90 w-[8%]">Storage</th>
                  <th className="text-left px-4 py-3 bg-slate-100/90 w-[7%]">RAM</th>
                  <th className="text-right px-4 py-3 bg-slate-100/90 w-[15%]">Price</th>
                  <th className="text-center px-4 py-3 bg-slate-100/90 w-[9%]">Stock</th>
                  <th className="text-right px-4 py-3 bg-slate-100/90 w-[13%]">{canOrder ? "Action" : ""}</th>
                </tr>
              </thead>
              <tbody>
                {loading && products.length === 0 ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-slate-100 animate-pulse">
                      <td className="px-4 py-3"><Skeleton className="h-4 w-48 mb-1.5" /><Skeleton className="h-3 w-28" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-12" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-12" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-12" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-4 w-20 ml-auto" /></td>
                      <td className="px-4 py-3 text-center"><Skeleton className="h-5 w-16 mx-auto" /></td>
                      <td className="px-4 py-3"><Skeleton className="h-8 w-16 ml-auto" /></td>
                    </tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-16 text-sm text-slate-500">No products match.</td></tr>
                ) : (
                  paginatedItems.map(p => <ProductRow key={p.product_id} p={p} />)
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Toolbar */}
          {filtered.length > PAGE_SIZE && (
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="text-xs text-slate-500">
                Showing <span className="font-medium">{(page - 1) * PAGE_SIZE + 1}</span> to <span className="font-medium">{Math.min(page * PAGE_SIZE, filtered.length)}</span> of <span className="font-medium">{filtered.length}</span> products (100 per page)
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 text-xs"
                >
                  Previous
                </Button>
                <span className="text-xs text-slate-600 px-2 font-medium">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-8 text-xs"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* ─── MOBILE: card grid ─────────────────────────────────────────────────── */}
      <div className="lg:hidden">
        <div className="grid grid-cols-2 gap-3">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <Card key={i} className="p-3 sm:p-4 flex flex-col space-y-3 animate-pulse">
                <Skeleton className="aspect-square w-full rounded-md" />
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-9 w-full rounded-md" />
              </Card>
            ))
          ) : filtered.length === 0 ? (
            <div className="col-span-full text-center py-16 text-sm text-slate-500">No products match.</div>
          ) : (
            paginatedItems.map(p => <ProductCard key={p.product_id} p={p} />)
          )}
        </div>
        {/* Mobile Pagination */}
        {filtered.length > PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="h-8 text-xs"
            >
              Prev
            </Button>
            <span className="text-xs text-slate-600 font-medium">
              {page} / {totalPages} (100/p)
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="h-8 text-xs"
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* Floating cart (canOrder only) */}
      {canOrder && cartCount > 0 && (
        <div className="fixed bottom-6 right-6 z-40 bg-white shadow-lg border border-slate-200 rounded-lg px-5 py-3 flex items-center gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Cart</div>
            <div className="text-sm font-medium">{cartCount} items · {fmtINR(cartTotal)}</div>
          </div>
          <Button onClick={goToBuilder} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="floating-cart-btn">
            Review &amp; submit PO
          </Button>
        </div>
      )}
    </div>
  );
}
